import { decode } from '@msgpack/msgpack';
import { leftPad } from '/modules/utilities/string.js';
import { substituteURLParametersInPath } from './utilities.js';

const MAX_METADATA_SIZE_IN_DIGITS = 6;
const requestTypes = {
  REQUEST: 'REQUEST',
  AUTHORIZATION: 'AUTHORIZATION',
  ASSOCIATE_WITH_OBSERVER: 'ASSOCIATE_WITH_OBSERVER'
};

const NOT_CONNECTED_ERROR = 'WebSocket not connected';

const connectionTimeoutMilliseconds = 30000;
const tabConnectTimeoutMilliseconds = 60000;
const leaderAttemptIntervalMilliseconds = 100;
const requestTimeoutMilliseconds = 120000;
const heartBeatIntervalMilliseconds = 30000;

function getTime() {
  return new Date().toLocaleTimeString('en-US', { hour12: false }) + '.' + String(new Date().getMilliseconds()).padStart(3, '0');
}

function getAuthorizationToken() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'authorizationToken') return decodeURIComponent(value);
  }
  return null;
}

function getObserverId() {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'ht-uoid') return decodeURIComponent(value);
  }
  return null;
}

const tabConnectStatus = {
  resolve: null,
  reject: null,
  resolved: false,
  rejected: false,
  timeout: null
};

class MultiplexedWebSocketClient {
  constructor({ url, version, onConnected, onDisconnected, onEvent, onAction, onServerCallWhenOffline, logger = console, onValidAuth = () => {}, onInvalidAuth = () => {} }) {
    this.url = url;
    this.version = version.startsWith('/') ? version : `/${version}`;
    this.onConnected = onConnected;
    this.onDisconnected = onDisconnected;
    this.onEvent = onEvent;
    this.onAction = onAction;
    this.onServerCallWhenOffline = onServerCallWhenOffline;

    this.replyIdRequestMap = {};
    this.sentMessagesCount = 0;
    this.reconnectionAttempt = 0;
    this.reconnectInterval = 100;
    this.associateWithObserverPayload = null;
    this.tabConnectStatus = {
      ...tabConnectStatus
    };
    this._tabConnectResolver = null;
    this.serverActionSubscriptions = [];
    this.connected = false;
    this.initialized = false;
    this.tabId = crypto.randomUUID();
    this.bc = new BroadcastChannel('ws_channel');
    this.logger = logger;
    this.onValidAuth = onValidAuth;
    this.onInvalidAuth = onInvalidAuth;
    this.isLeader = false;
    this.ready = false;
    this.logger.debug(`[ws][${getTime()}] initialized`, { tabId: this.tabId });
    this._setupBroadcastListeners();
    navigator.locks.request('ws-leader', { mode: 'exclusive', ifAvailable: true }, lock => {
      if (!lock) {
        this._tryInitializeFlags();
        return;
      }
      this._becomeLeader();
      return new Promise(() => {});
    });
    setInterval(() => {
      if (this.isLeader) return;
      navigator.locks.request('ws-leader', { mode: 'exclusive', ifAvailable: true }, lock => {
        if (!lock) return;
        this._becomeLeader();
        return new Promise(() => {});
      });
    }, leaderAttemptIntervalMilliseconds);
    this.startHeartBeat();
  }

  _tryInitializeFlags() {

    this.bc.postMessage({
      type: 'ws-status-check',
      from: this.tabId
    });

  }

  startHeartBeat() {

    this.heartBeatTimerReference = setInterval(() => {

      if (this.isLeader === false) {
        return;
      }

      if (this.socket?.readyState !== WebSocket.OPEN) {
        return;
      }

      if (this.ready === false) {
        return;
      }

      this.socket.send(JSON.stringify({ type: 'heartbeat', time: Date.now() }));

    }, heartBeatIntervalMilliseconds);

  }

  _waitUntilSockedConnected() {

    return new Promise((resolve, reject) => {

      let startTime = Date.now();

      let intervalReference = setInterval(() => {

        if ((Date.now() - startTime) >= connectionTimeoutMilliseconds) {
          clearInterval(intervalReference);
          return reject(new Error(`Unable to connect in ${connectionTimeoutMilliseconds/1000} seconds`));
        }

        if (this.ready === false) {
          return;
        }

        clearInterval(intervalReference);

        resolve();

      }, 100);

    });

  }

  _resetTabConnectStatus() {

    this.tabConnectStatus = { ...tabConnectStatus };

  }

  _connectTab(attempt = 0) {

    this.tabConnectStatus.timeout = setTimeout(() => {

      if (this.tabConnectStatus.resolved === true) {
        return;
      }

      this.tabConnectStatus.rejected = true;

      this.tabConnectStatus.reject(new Error('Tab connect timedout'));

    }, tabConnectTimeoutMilliseconds);

    this._tabConnectResolver = (data) => {

      clearTimeout(this.tabConnectStatus.timeout);
      if (this.tabConnectStatus.rejected === true) {
        this.logger.debug(`[ws][${getTime()}] resetting connect status after rejection`);
        this._resetTabConnectStatus();
        return;
      }
      this.logger.debug(`[ws][${getTime()}] not rejected already. Resuming!`, { ...this.tabConnectStatus });
      this.tabConnectStatus.resolved = true;

      if (data.error === true) {
        if (data.client === true && data.attempt < 10) {
          setTimeout(() => this._connectTab(data.attempt + 1), 500);
          return;
        } else {
          this.tabConnectStatus.reject(data.payload);
        }
      } else {
        this.tabConnectStatus.resolve(data.payload);
      }

      this.logger.debug(`[ws][${getTime()}] ws-tab-connect-response`, data);

      this.logger.debug(`[ws][${getTime()}] resetting connect status after server response`);
      this._resetTabConnectStatus();

    };

    const request = {
      type: 'ws-tab-connect',
      from: this.tabId,
      token: getAuthorizationToken(),
      observerId: getObserverId(),
      attempt
    };

    if (this.isLeader === true) {
      this.socket.send(JSON.stringify(request));
    } else {
      this.bc.postMessage(request);
    }

    this.logger.debug(`[ws][${getTime()}] ws-tab-connect`, { from: this.tabId });

  }

  async connect() {

    await this._waitUntilSockedConnected();

    return new Promise((resolve, reject) => {

      this.tabConnectStatus.resolve = resolve;
      this.tabConnectStatus.reject = reject;
      this.logger.debug(`[ws][${getTime()}] Connecting tab from connect`, { from: this.tabId });
      this._connectTab();
    });

  }

  _handleServerResponse(data) {

    const {
      replyId,
      payload,
      error
    } = data;

    const request = this.replyIdRequestMap[replyId];
    if (request) {
      clearTimeout(request.timeout);
      const path = request.path;
      const durationInMs = Date.now() - request.dateTime;
      let duration;
      if (durationInMs < 1000) {
        duration = `${durationInMs}ms`;
      } else {
        duration = `${(durationInMs / 1000).toFixed(1)}s`;
      }
      if (error === false) {
        this.logger.debug(`[ws][${getTime()}] request resolved: ${replyId}`, { replyId, path, payload, duration });
        request.resolve(payload);
      } else {
        this.logger.debug(`[ws][${getTime()}] request rejected: ${replyId}`, { replyId, path, payload, duration });
        request.reject(payload);
      }
      delete this.replyIdRequestMap[replyId];
    }

  }

  replayPendingRequests() {

    const pendingRequests = {
      ...this.replyIdRequestMap
    };

    this.logger.debug(`[ws][${getTime()}] replaying pending requests`, {
      pendingRequests: Object.keys(pendingRequests).length
    });

    this.replyIdRequestMap = {};

    for (let replyId in pendingRequests) {

      const request = pendingRequests[replyId];

      clearTimeout(request.timeout);

      if (request.method !== 'GET') {
        request.reject(new Error('Connection broke'));
        continue;
      }

      this.doSendRequest(request.message, request.message.type, request.resolve, request.resolve);

    }

  }

  tryResume() {

    this.replayPendingRequests();

    if (this.tabConnectStatus.timeout !== null) {
      clearTimeout(this.tabConnectStatus.timeout);
      this.logger.debug(`[ws][${getTime()}] Connecting tab from resume`, { from: this.tabId });
      this._connectTab();
    }

  }

  _setupBroadcastListeners() {
    this._statusChangeTimer = null;
    this._statusChangeTimer = null;

    this.bc.onmessage = (event) => {
      const { type, payload, replyId, from, connected } = event.data;

      if (type === 'ws-event') {
        this.onEvent(payload);
      } else if (type === 'ws-action') {
        this.onAction(payload);
      } else if (type === 'ws-response' && from === this.tabId) {
        this._handleServerResponse(payload);
      } else if (type === 'ws-status') {
        this.logger.debug(`[ws][${getTime()}] connection status`, { connected });
        this.ready = false;
        clearTimeout(this._statusChangeTimer);
        this._statusChangeTimer = setTimeout(() => {
          const wasConnected = this.connected;
          this.connected = connected;
          this.logger.debug(`[ws][${getTime()}] connection status changed`, { wasConnected, connected });
          if (!wasConnected && connected) this.onConnected();
          if (wasConnected && !connected) this.onDisconnected();
        }, 200);
      } else if (type === requestTypes.REQUEST && this.isLeader) {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(event.data));
        } else {
          this.bc.postMessage({
            type: 'ws-response',
            replyId,
            from,
            payload: { error: true, payload: new Error(NOT_CONNECTED_ERROR) }
          });
        }
      } else if (type === 'ws-send-only' && this.isLeader && this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(payload instanceof Blob ? payload : JSON.stringify(payload));
      } else if (type === 'ws-tab-connect' && this.isLeader) {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(event.data));
        } else {
          this.bc.postMessage({
            type: 'ws-tab-connect-response',
            replyId,
            from,
            payload: {
              client: true,
              error: true,
              attempt: event.data.attempt,
              payload: new Error(NOT_CONNECTED_ERROR)
            }
          });
        }
      } else if (type === 'ws-tab-connect-response' && from === this.tabId) {
        this._tabConnectResolver(payload);
      } else if (type === 'ready') {
        this.ready = true;
        this.tryResume();
      } else if (type === 'ws-status-check') {
        if (this.isLeader == true) {
          this.bc.postMessage({
            type: 'ws-status-check-response',
            from,
            payload: { connected: this.connected, ready: this.ready }
          });
        }
      } else if (type === 'ws-status-check-response') {
        this.connected = payload.connected;
        this.ready = payload.ready;
      } else if (type === 'authorization-invalid') {
        this.onInvalidAuth();
      }
    };
  }

  _becomeLeader() {
    this.isLeader = true;
    this.logger.debug(`[ws][${getTime()}] becoming leader`);
    this.socket = new WebSocket(this.url);
    this.socket.binaryType = 'arraybuffer';

    this.socket.onopen = () => {
      this.connected = true;
      this.ready = false;
      this.reconnectionAttempt = 0;
      this.logger.debug(`[ws][${getTime()}] socket opened`);
      this.bc.postMessage({ type: 'ws-status', connected: true });
    };

    this.socket.onmessage = (event) => {

      const msg = decode(event.data);
      if (msg.type === 'EVENT') {
        const payload = msg.payload;
        this.onEvent(payload);
        this.bc.postMessage({ type: 'ws-event', payload });
      } else if (msg.type === 'ACTION') {
        const payload = msg.payload;
        this.onAction(payload);
        this.bc.postMessage({ type: 'ws-action', payload });
      } else if (msg.replyId && msg.from) {
        if (msg.from === this.tabId) {
          this._handleServerResponse(msg);
        } else {
          this.bc.postMessage({ type: 'ws-response', replyId: msg.replyId, from: msg.from, payload: msg });
        }
      } else if (msg.type === 'ws-tab-connect-response') {
        if (msg.from === this.tabId) {
          this._tabConnectResolver(msg.payload);
        } else {
          this.bc.postMessage(msg);
        }
      } else if (msg.type === 'READY_TO_AUTHORIZE') {
        this.ready = true;
        this.tryResume();
        this.bc.postMessage({ type: 'ready' });
      }
    };

    this.socket.onclose = (reason) => {
      this.logger.debug(`[ws][${getTime()}] socket closed`, { reason });
      this.bc.postMessage({ type: 'ws-status', connected: false });
      this.reconnectionAttempt++;
      setTimeout(() => this._becomeLeader(), this.reconnectionAttempt * this.reconnectInterval);
    };
  }

  doSendRequest(request, type, resolve, reject) {

    const replyId = ++this.sentMessagesCount;
    const message = {
      ...request,
      type,
      replyId,
      from: this.tabId,
      headers: {
        authorization: getAuthorizationToken()
      }
    };
    this.logger.debug(`[ws][${getTime()}] sending request: ${replyId}`, message);

    const payload = message;

    const errorResponseGen = (error) => {
      this.logger.debug(`[ws][${getTime()}] request error: ${replyId}`, { replyId, request, type, error });
      reject(error);
      delete this.replyIdRequestMap[replyId];
    };

    const timeout = setTimeout(() => {
      errorResponseGen(new Error('Request timeout'));
    }, request.timeout || requestTimeoutMilliseconds);

    this.replyIdRequestMap[replyId] = {
      resolve,
      reject,
      path: message.pathTemplate,
      timeout,
      dateTime: Date.now(),
      message
    };

    if (this.isLeader === true) {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify(payload));
      } else {
        clearTimeout(timeout);
        errorResponseGen(new Error(NOT_CONNECTED_ERROR));
      }
    } else {
      this.bc.postMessage(payload);
    }

  }

  sendRequest(request, type) {
    if (!this.ready) {
      this.logger.debug(`[ws][${getTime()}] sendRequest failed: WebSocket not connected`);
      throw new Error(NOT_CONNECTED_ERROR);
    }

    return new Promise((resolve, reject) => {

      this.doSendRequest(request, type, resolve, reject);

    });
  }

  sendAPIRequest(method, path, urlParameters = {}, queryOptions = {}, payload = {}) {
    const _path = substituteURLParametersInPath(path, urlParameters);
    return this.sendRequest({
      method,
      pathTemplate: path,
      url: this.version + _path,
      urlParameters,
      queryOptions,
      body: payload
    }, requestTypes.REQUEST);
  }

  sendRaw(service, method, options, blobField, blob) {
    if (!this.ready) {
      this.logger.debug(`[ws][${getTime()}] sendRaw failed: WebSocket not connected`);
      throw new Error(NOT_CONNECTED_ERROR);
    }

    return new Promise((resolve, reject) => {
      const replyId = ++this.sentMessagesCount;
      this.replyIdRequestMap[replyId] = { resolve, reject };

      const metadata = { service, method, options, blobField, replyId, from: this.tabId };
      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const sizeBlob = new Blob(['|', leftPad(metadataBlob.size, MAX_METADATA_SIZE_IN_DIGITS), '|'], { type: 'text/plain' });
      const payloadBlob = new Blob([sizeBlob, metadataBlob, blob]);

      this.bc.postMessage({ type: 'ws-send-only', payload: payloadBlob });
    });
  }

  _handleServerMessage(message) {
    if (message.type === 'EVENT') this.onEvent(message.payload);
    else if (message.type === 'ACTION') this.onAction(message);
  }

  tryAssociateWithObserverId(payload) {
    this.associateWithObserverPayload = payload;
    this.sendRequest(payload, requestTypes.ASSOCIATE_WITH_OBSERVER);
  }

  subscribeForServerActions(path) {
    if (!this.serverActionSubscriptions.includes(path)) {
      this.serverActionSubscriptions.push(path);
      if (this.ready) this.bc.postMessage({ type: 'ws-send-only', payload: { type: 'SERVER_ACTION_SUBSCRIPTION', path } });
    }
  }

  getBufferedDataSize() {
    const size = this.socket?.bufferedAmount || 0;
    return size;
  }

  disconnect() {
    this.logger.debug(`[ws][${getTime()}] disconnecting`);
    this._shutdown();
  }

  logout() {

    this.connect();
    this.bc.postMessage({
      type: 'authorization-invalid'
    });

  }

}

export default MultiplexedWebSocketClient;